import { FeedType, PaginatedResult } from 'graphql/types'
import { VirtualRows, useRestoreScroll, useVirtual } from 'lib/react-virtual'
import { ComponentProps } from 'lib/theme'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import { PaginatedPosts } from 'models/PaginatedPosts'
import { Flex } from 'primitives/Flex'
import React, { useRef } from 'react'
import { usePrevious, useWindowSize } from 'react-use'
import { FeedContainer } from 'shared/FeedContainer'
import { FeedItem } from '../FeedItem'

export interface ThreadsProps extends ComponentProps<typeof Flex> {
  paginatedPosts: PaginatedPosts
}

export const _Feed: React.FC<ThreadsProps> = ({ css, paginatedPosts }) => {
  const bodyRef = useRef(window.document.body)

  const { width: windowWidth } = useWindowSize()

  const feedItems = paginatedPosts?.posts
  const feedLoading = paginatedPosts?.loading

  const rowVirtualizer = useVirtual({
    overscan: 3,
    count: feedItems?.length + 1, // add 1 row for loader/feedend
    getScrollElement: () => bodyRef.current,
    getItemKey: (index) => feedItems[index]?.postId ?? index,
    estimateSize: (index) => feedItems[index]?.estimatePostCardHeight(windowWidth) ?? 50, // feed end
    onNearEnd: () => {
      if (feedItems.length) paginatedPosts.fetchFeed()
    }
  })
  useRestoreScroll({
    feedItems,
    rowVirtualizer,
    scrollContainerRef: bodyRef,
    scrollIndex: paginatedPosts?.scrollIndex,
    setScrollIndex: (index) => paginatedPosts.setScrollIndex(index)
  })

  return (
    <FeedContainer css={css}>
      <VirtualRows v={rowVirtualizer}>
        {({ virtualRow: { index }, measureElementRow }) => {
          return (
            <FeedItem
              rootRef={bodyRef}
              post={feedItems[index]}
              index={index}
              feedItems={4}
              loading={feedLoading && paginatedPosts.posts && index >= paginatedPosts.posts.length}
              error={paginatedPosts.error}
              onContentLoaded={measureElementRow}
            />
          )
        }}
      </VirtualRows>
    </FeedContainer>
  )
}

export const Feed = observer(_Feed)
